import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, shareReplay, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AngularFirestore } from '@angular/fire/compat/firestore';

@Injectable({
  providedIn: 'root',
})
export class ProjectsDataService {
  private apiUrl = environment.api.url;
  private projectsCache$: Observable<any[]> | null = null;
  private cacheTimestamp: number = 0;
  private allProjectsCache$: Observable<any[]> | null = null;
  private allProjectsCacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  constructor(
    private http: HttpClient,
    private afs: AngularFirestore,
  ) {}

  /**
   * Obtiene la lista de proyectos VISIBLES (hide == false).
   * MODO LEGACY: Obtiene datos directamente de Firestore ('investments')
   * para compatibilidad con backend ramses_development.
   *
   * @param forceRefresh Fuerza la recarga ignorando el caché
   */
  getProjects(forceRefresh: boolean = false): Observable<any[]> {
    const isCacheValid = Date.now() - this.cacheTimestamp < this.CACHE_TTL;

    if (!this.projectsCache$ || forceRefresh || !isCacheValid) {
      console.log(
        '🔄 [ProjectsDataService] Cargando proyectos de Firestore (Legacy Mode)...',
      );
      this.projectsCache$ = this.getProjectsFromFirestore().pipe(
        tap(() => {
          console.log(
            '✅ [ProjectsDataService] Datos cargados desde Firestore',
          );
          this.cacheTimestamp = Date.now();
        }),
        shareReplay(1),
      );
    }

    return this.projectsCache$;
  }

  private getProjectsFromFirestore(): Observable<any[]> {
    return this.afs
      .collection('investments', (ref) => ref.where('hide', '==', false))
      .valueChanges()
      .pipe(
        tap(() =>
          console.log(
            '🔥 [ProjectsDataService] Datos recuperados de colección inversiones',
          ),
        ),
      );
  }

  /**
   * Obtiene TODOS los proyectos (incluyendo ocultos).
   * Útil para funcionalidades de administración como cambiar visibilidad.
   *
   * @param forceRefresh Fuerza la recarga ignorando el caché
   */
  getAllProjects(forceRefresh: boolean = false): Observable<any[]> {
    const isCacheValid = Date.now() - this.allProjectsCacheTimestamp < this.CACHE_TTL;

    if (!this.allProjectsCache$ || forceRefresh || !isCacheValid) {
      console.log('🔄 [ProjectsDataService] Cargando TODOS los proyectos (incluidos ocultos)...');
      this.allProjectsCache$ = this.afs
        .collection('investments')
        .get() // <-- Usa get() para hacer una única petición en lugar de valueChanges que abre un listener permanente
        .pipe(
          map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) }))),
          tap(() => {
            console.log('✅ [ProjectsDataService] Todos los proyectos cargados desde Firestore');
            this.allProjectsCacheTimestamp = Date.now();
          }),
          shareReplay(1)
        );
    }
    return this.allProjectsCache$;
  }

  clearCache() {
    this.projectsCache$ = null;
    this.cacheTimestamp = 0;
    this.allProjectsCache$ = null;
    this.allProjectsCacheTimestamp = 0;
  }
}
